/**
 * Shared session-lifecycle contract for every authenticated surface (admin,
 * ops/staff and customer portal).
 *
 * A "session scope" is bound to one authenticated identity (a Firebase uid or
 * `null` for anonymous). Whenever the identity changes the previous scope is
 * aborted, so
 *  - requests that were started for the previous account are cancelled through
 *    their AbortSignal,
 *  - late asynchronous results can be rejected by checking `scope.isCurrent()`
 *    before committing them, and
 *  - query/mutation caches can be dropped synchronously before the next
 *    account renders.
 *
 * Only the opaque uid is stored here. Bearer tokens, signed URLs and profile
 * data never enter the scope, query keys or any persisted state.
 */
export interface SessionScope {
  readonly generation: number;
  readonly uid: string | null;
  readonly signal: AbortSignal;
  /** True only while this scope is the lifecycle's active scope. */
  isCurrent(): boolean;
}

export type SessionChangeListener = (next: SessionScope, previous: SessionScope) => void;

export interface SessionLifecycle {
  readonly current: SessionScope;
  /** Always starts a fresh scope (aborting the previous one), even for the same uid. */
  begin(uid: string | null): SessionScope;
  /** Reuses the current scope when it already belongs to `uid`, otherwise begins a new one. */
  ensure(uid: string | null): SessionScope;
  /** Ends the active session (anonymous scope). */
  end(): SessionScope;
  subscribe(listener: SessionChangeListener): () => void;
}

export const SESSION_ABORT_REASON = 'SESSION_ENDED';

export function createSessionAbortError(): Error {
  const error = new Error(SESSION_ABORT_REASON);
  error.name = 'AbortError';
  return error;
}

/** True for cancellations caused by a session change or any other AbortSignal. */
export function isAbortError(error: unknown): boolean {
  return (error instanceof Error || (typeof DOMException !== 'undefined' && error instanceof DOMException))
    && error.name === 'AbortError';
}

export function createSessionLifecycle(): SessionLifecycle {
  const listeners = new Set<SessionChangeListener>();
  let controller = new AbortController();
  let generation = 0;
  let current: SessionScope;

  const makeScope = (uid: string | null): SessionScope => {
    const scope: SessionScope = {
      generation,
      uid,
      signal: controller.signal,
      isCurrent: () => current === scope,
    };
    return scope;
  };

  current = makeScope(null);

  const begin = (uid: string | null): SessionScope => {
    const previous = current;
    controller.abort(createSessionAbortError());
    controller = new AbortController();
    generation += 1;
    current = makeScope(uid);
    listeners.forEach((listener) => listener(current, previous));
    return current;
  };

  return {
    get current() {
      return current;
    },
    begin,
    ensure: (uid) => (current.uid === uid ? current : begin(uid)),
    end: () => begin(null),
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

/** Structural subset of TanStack's QueryClient so this package stays framework-free. */
export interface ClearableQueryClient {
  cancelQueries(): Promise<unknown>;
  clear(): void;
}

/**
 * Cancels in-flight queries and removes every cached query and mutation as soon
 * as the session identity changes (sign-out, account switch, role change or
 * provider unmount). The reset is synchronous so the next account can never
 * render the previous account's rows, not even for a single frame.
 */
export function bindQueryClientToSession(lifecycle: SessionLifecycle, queryClient: ClearableQueryClient): () => void {
  return lifecycle.subscribe(() => {
    void queryClient.cancelQueries();
    queryClient.clear();
  });
}
